import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, ListGroup, Modal, Spinner } from "react-bootstrap";
import { FaChevronRight, FaFolder, FaRegStar, FaSearch, FaStar } from "react-icons/fa";
import {
  getFolderChildren,
  getPinnedDestinations,
  getRecentDestinations,
  getSiteDrives,
  pinDestination,
  searchSharePointSites,
  unpinDestination,
  PinDestinationRequest,
  SharePointDestination,
  SharePointDrive,
  SharePointFolderItem,
  SharePointSite,
} from "@/api/emailFilingApi";

interface SharePointDestinationPickerProps {
  show: boolean;
  onClose: () => void;
  onSelect: (destination: PinDestinationRequest) => void;
}

interface BreadcrumbEntry {
  folderItemId?: string;
  name: string;
}

const SharePointDestinationPicker: React.FC<SharePointDestinationPickerProps> = ({ show, onClose, onSelect }) => {
  const [pinned, setPinned] = useState<SharePointDestination[]>([]);
  const [recent, setRecent] = useState<SharePointDestination[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [searchText, setSearchText] = useState("");
  const [sites, setSites] = useState<SharePointSite[]>([]);
  const [searching, setSearching] = useState(false);

  const [selectedSite, setSelectedSite] = useState<SharePointSite | null>(null);
  const [drives, setDrives] = useState<SharePointDrive[]>([]);
  const [selectedDrive, setSelectedDrive] = useState<SharePointDrive | null>(null);

  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([]);
  const [folders, setFolders] = useState<SharePointFolderItem[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [pinningKey, setPinningKey] = useState<string | null>(null);

  useEffect(() => {
    if (!show) return;
    setError(null);
    void loadPinnedAndRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadPinnedAndRecent = async () => {
    try {
      const [pinnedResult, recentResult] = await Promise.all([getPinnedDestinations(), getRecentDestinations()]);
      setPinned(pinnedResult);
      setRecent(recentResult);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load your saved destinations.");
    }
  };

  const handleSearchSites = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!searchText.trim()) return;
    setSearching(true);
    setError(null);
    try {
      setSites(await searchSharePointSites(searchText.trim()));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to search SharePoint sites.");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSite = async (site: SharePointSite) => {
    setSelectedSite(site);
    setSelectedDrive(null);
    setFolders([]);
    setBreadcrumb([]);
    setError(null);
    try {
      setDrives(await getSiteDrives(site.siteId));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load document libraries for this site.");
    }
  };

  const handleSelectDrive = async (drive: SharePointDrive) => {
    setSelectedDrive(drive);
    setBreadcrumb([{ folderItemId: undefined, name: drive.driveName }]);
    await loadFolders(drive.driveId, undefined);
  };

  const loadFolders = async (driveId: string, folderItemId: string | undefined) => {
    setLoadingFolders(true);
    setError(null);
    try {
      setFolders(await getFolderChildren(driveId, folderItemId));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load folders.");
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleOpenFolder = async (folder: SharePointFolderItem) => {
    if (!selectedDrive) return;
    setBreadcrumb((prev) => [...prev, { folderItemId: folder.itemId, name: folder.name }]);
    await loadFolders(selectedDrive.driveId, folder.itemId);
  };

  const handleBreadcrumbClick = async (index: number) => {
    if (!selectedDrive) return;
    const target = breadcrumb[index];
    setBreadcrumb(breadcrumb.slice(0, index + 1));
    await loadFolders(selectedDrive.driveId, target.folderItemId);
  };

  const buildDestination = (folder?: SharePointFolderItem): PinDestinationRequest | null => {
    if (!selectedSite || !selectedDrive) return null;
    const current = breadcrumb[breadcrumb.length - 1];
    const target = folder ?? current;
    return {
      siteId: selectedSite.siteId,
      siteName: selectedSite.displayName,
      siteUrl: selectedSite.webUrl,
      driveId: selectedDrive.driveId,
      driveName: selectedDrive.driveName,
      folderItemId: folder ? folder.itemId : current?.folderItemId,
      folderName: target?.name ?? selectedDrive.driveName,
      folderPath: [...breadcrumb.map((b) => b.name), ...(folder ? [folder.name] : [])].join(" / "),
    };
  };

  const handleSelectCurrentFolder = () => {
    const destination = buildDestination();
    if (destination) onSelect(destination);
  };

  const handleSelectFolder = (folder: SharePointFolderItem) => {
    const destination = buildDestination(folder);
    if (destination) onSelect(destination);
  };

  const handleSelectSaved = (destination: SharePointDestination) => {
    onSelect({
      siteId: destination.siteId,
      siteName: destination.siteName,
      siteUrl: destination.siteUrl,
      driveId: destination.driveId,
      driveName: destination.driveName,
      folderItemId: destination.folderItemId,
      folderName: destination.folderName,
      folderPath: destination.folderPath,
    });
  };

  const isPinned = (driveId: string, folderItemId?: string) =>
    pinned.some((p) => p.driveId === driveId && (p.folderItemId || "") === (folderItemId || ""));

  const handleTogglePinCurrentFolder = async (folder?: SharePointFolderItem) => {
    const destination = buildDestination(folder);
    if (!destination) return;
    const key = `${destination.driveId}|${destination.folderItemId || ""}`;
    setPinningKey(key);
    try {
      const existing = pinned.find((p) => p.driveId === destination.driveId && (p.folderItemId || "") === (destination.folderItemId || ""));
      if (existing?.destinationGuid) {
        await unpinDestination(existing.destinationGuid);
      } else {
        await pinDestination(destination);
      }
      await loadPinnedAndRecent();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to update pinned destinations.");
    } finally {
      setPinningKey(null);
    }
  };

  const renderSavedList = (destinations: SharePointDestination[], emptyText: string) =>
    destinations.length === 0 ? (
      <div className="text-muted small py-2">{emptyText}</div>
    ) : (
      <ListGroup variant="flush">
        {destinations.map((destination) => (
          <ListGroup.Item key={`${destination.driveId}|${destination.folderItemId || ""}`} action onClick={() => handleSelectSaved(destination)} className="d-flex justify-content-between align-items-center">
            <div className="min-w-0">
              <div className="fw-semibold text-break">{destination.folderName}</div>
              <div className="small text-muted text-break">{destination.siteName} / {destination.driveName}</div>
            </div>
            {destination.isPinned && <FaStar className="text-warning flex-shrink-0 ms-2" aria-label="Pinned" />}
          </ListGroup.Item>
        ))}
      </ListGroup>
    );

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>File to SharePoint</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

        <div className="fw-semibold mb-1">Pinned</div>
        {renderSavedList(pinned, "No pinned destinations yet — pin a folder while browsing below.")}

        <div className="fw-semibold mb-1 mt-3">Recent</div>
        {renderSavedList(recent, "Locations you file into will show up here.")}

        <div className="fw-semibold mb-2 mt-3">Browse / Search</div>
        <Form className="d-flex gap-2 mb-2" onSubmit={handleSearchSites}>
          <Form.Control placeholder="Search SharePoint sites" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          <Button type="submit" variant="outline-secondary" disabled={searching}>
            {searching ? <Spinner animation="border" size="sm" /> : <FaSearch />}
          </Button>
        </Form>

        {sites.length > 0 && !selectedSite && (
          <ListGroup variant="flush" className="mb-2">
            {sites.map((site) => (
              <ListGroup.Item key={site.siteId} action onClick={() => void handleSelectSite(site)}>
                {site.displayName}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}

        {selectedSite && !selectedDrive && (
          <>
            <div className="small text-muted mb-1">{selectedSite.displayName} — choose a document library</div>
            <ListGroup variant="flush" className="mb-2">
              {drives.map((drive) => (
                <ListGroup.Item key={drive.driveId} action onClick={() => void handleSelectDrive(drive)}>
                  {drive.driveName}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>
        )}

        {selectedDrive && (
          <>
            <div className="d-flex align-items-center flex-wrap gap-1 small mb-2">
              {breadcrumb.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <FaChevronRight className="text-muted" />}
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void handleBreadcrumbClick(index)}>{crumb.name}</button>
                </React.Fragment>
              ))}
            </div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button size="sm" variant="primary" onClick={handleSelectCurrentFolder}>Select this folder</Button>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={pinningKey !== null}
                onClick={() => void handleTogglePinCurrentFolder()}
              >
                {isPinned(selectedDrive.driveId, breadcrumb[breadcrumb.length - 1]?.folderItemId) ? <FaStar className="text-warning" /> : <FaRegStar />} Pin this folder
              </Button>
            </div>
            {loadingFolders ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : folders.length === 0 ? (
              <div className="text-muted small py-2">No subfolders here.</div>
            ) : (
              <ListGroup variant="flush">
                {folders.map((folder) => (
                  <ListGroup.Item key={folder.itemId} className="d-flex justify-content-between align-items-center">
                    <button type="button" className="btn btn-link p-0 text-start flex-grow-1 text-decoration-none" onClick={() => void handleOpenFolder(folder)}>
                      <FaFolder className="me-2" />{folder.name}
                      {folder.hasChildFolders && <Badge bg="light" text="dark" className="ms-2">has subfolders</Badge>}
                    </button>
                    <Button size="sm" variant="outline-primary" onClick={() => handleSelectFolder(folder)}>Select</Button>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SharePointDestinationPicker;
